<template>
  <div class="reviews-archive">
    <div
      v-if="isLoading || !(reviewsToShow.length > 0)"
      key="loading"
      v-html="loadingHTML"
    />
    <div v-else class="reviews-archive__list-container">
      <transition-group
        ref="grid"
        class="reviews-archive__list"
        name="reviews-archive__list"
        tag="div"
      >
        <review-card
          v-for="(review, index) of reviewsToShow"
          :key="`${review.id}-${index}`"
          :post="review"
          variant="card"
        />
      </transition-group>
    </div>
    <button
      v-if="!maxedOut"
      class="btn cta reviews-archive__button"
      @click="showMoreReviews"
      v-text="texts.showMoreRatingsText"
    />

    <review-modal v-if="modalReview" />
  </div>
</template>

<script>
import debounce from 'lodash-es/debounce';
import { sprintf } from 'sprintf-js';
import { mapState } from 'vuex';
import { formatAverageRating, formatNumberOfRatings } from '../misc/helpers';
import { useIsMobile } from './../../composables/useIsMobile';
import ReviewCard from './ReviewCard.vue';
import ReviewModal from './ReviewModal.vue';

export default {
  name: 'ReviewsArchive',

  components: {
    ReviewCard,
    ReviewModal,
  },

  setup() {
    const { isMobile, isTablet } = useIsMobile();
    return { isMobile, isTablet };
  },

  data() {
    const {
      dataset: { hideNumberOfReviews, reviewsType },
    } = this.$mountEl;
    const { innerHTML } = this.$mountElSnapshot;

    return {
      texts: theme_vars.trustpilotArchive,
      hideNumberOfReviews,
      reviewsData: null,
      isLoading: true,
      pagesShown: 1,
      loadingHTML: innerHTML,
      reviewsType,
    };
  },

  computed: {
    ...mapState('reviews', ['modalReview']),

    averageRating() {
      return this.formatAverageRating(this.reviewsData.average_rating);
    },

    numberOfRatings() {
      return this.formatNumberOfRatings(this.reviewsData.total_reviews);
    },

    ratingString() {
      const { ratingText } = this.texts;
      return sprintf(ratingText, this.averageRating, this.numberOfRatings);
    },

    pagedReviews() {
      if (!this.reviewsData) {
        return [];
      }

      const source =
        this.reviewsData.reviews_for_resource_type ||
        this.reviewsData.reviews ||
        [];

      const paged = 24; // 24 reviews per page
      const reviews = [];

      const allReviews = Array.isArray(source) ? source : Object.values(source);

      for (let i = 0; i < allReviews.length; i += paged) {
        reviews.push(allReviews.slice(i, i + paged));
      }

      return reviews;
    },

    reviewsToShow() {
      if (this.pagedReviews.length === 0) {
        return [];
      }

      if (this.pagesShown === 1) {
        return this.pagedReviews[0];
      }

      const reviews = [];

      for (let i = 0; i < this.pagesShown; i++) {
        reviews.push(...this.pagedReviews[i]);
      }

      return reviews;
    },

    numPages() {
      return this.pagedReviews.length;
    },

    maxedOut() {
      return this.pagesShown >= this.numPages;
    },
  },

  mounted() {
    this.fetchReviewsData().then(() => {
      this.$nextTick(() => {
        if (!this.isMobile) {
          this.resizeAllGridItems();
        }

        this.isLoading = false;
      });
    });

    window.addEventListener('resize', debounce(this.resizeAllGridItems, 25));
  },

  methods: {
    formatAverageRating,
    formatNumberOfRatings,

    fetchReviewsData() {
      const market = theme_vars.currentAdminMarket.toLowerCase();

      return new Promise((resolve) => {
        let url = `${theme_vars.site_url}/wp-json/reviews/v1/market-overview/${market}/${theme_vars.locale}`;
        if (this.reviewsType) {
          url += `?type=${this.reviewsType}`;
        }

        fetch(url)
          .then((response) => response.json())
          .then((data) => {
            this.reviewsData = data;
            this.isLoading = false;
            return resolve();
          });
      });
    },

    showMoreReviews() {
      this.pagesShown++;

      this.$nextTick(() => {
        this.resizeAllGridItems();
      });
    },

    resizeAllGridItems() {
      if (!this.$refs.grid || !this.$refs.grid.$el) {
        return;
      }

      const grid = this.$refs.grid.$el;
      const allItems = grid.querySelectorAll('.review-card');

      if (this.isMobile) {
        for (const item of allItems) {
          item.style.gridRowEnd = 'auto';
          item.style.alignSelf = '';
        }
        return;
      }

      // Release each card from the grid cell so it lays out at its natural
      // content height; otherwise grid stretch would pin it to ~18px and
      // every span would compute as 1.
      for (const item of allItems) {
        item.style.gridRowEnd = 'auto';
        item.style.alignSelf = 'start';
      }

      const rowHeight = parseInt(
        window.getComputedStyle(grid).getPropertyValue('grid-auto-rows')
      );
      const rowGap = parseInt(
        window.getComputedStyle(grid).getPropertyValue('row-gap')
      );

      // Read all heights before writing, to keep this to a single layout pass.
      const heights = Array.from(allItems, (item) =>
        item.getBoundingClientRect().height
      );

      allItems.forEach((item, index) => {
        const rowSpan = Math.ceil(
          (heights[index] + rowGap) / (rowHeight + rowGap)
        );
        item.style.gridRowEnd = 'span ' + rowSpan;
        item.style.alignSelf = '';
      });
    },
  },
};
</script>
